// getShouldShowLivestreamCta.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import getMeData from '../selector/getMeData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getOperationData from '../selector/getOperationData.js';
import getNetworkingData from '../selector/getNetworkingData.js';

import {
  PRESET_SD,
  PRESET_PREVIEW,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';

/**
 * Select Get should show livestream cta by stream id
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} Return true if cta button should show.
 */
const getShouldShowLivestreamCta = createCachedSelector(
  state => getMeData(state, 'id'),
  (state, streamId) => getPrivateShowStatus(state, streamId),
  (state, streamId) => getLiveStreamData(state, [streamId], 'status'),
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'privateShow'], 'fundingId'),
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'presets', PRESET_SD], 'nbf'),
  (state, streamId) =>
    getLiveStreamData(
      state,
      [streamId, 'presets', PRESET_SD, 'counter'],
      'nbf'
    ),
  (state, streamId) =>
    getLiveStreamData(
      state,
      [streamId, 'presets', PRESET_SD, 'counter'],
      'exp'
    ),
  (state, streamId) =>
    getOperationData(state, ['liveStream', 'paid', streamId], 'streamToken'),
  (state, streamId) => {
    const sessionId = getLiveStreamData(state, [streamId], 'sessionId');
    return getOperationData(
      state,
      ['liveStream', 'paid', streamId, sessionId],
      'shouldSubmitStreamPaymentOnSdPreset'
    );
  },
  (state, streamId) => {
    const sessionId = getLiveStreamData(state, [streamId], 'sessionId');
    return getNetworkingData(
      state,
      ['submit', streamId, sessionId, 'payment'],
      'isFetching'
    );
  },
  (state, streamId) => {
    const sessionId = getLiveStreamData(state, [streamId], 'sessionId');
    return getNetworkingData(
      state,
      ['fetch', 'streamToken', sessionId],
      'isFetching'
    );
  },
  (
    meId,
    privateShowStatus,
    streamStatus,
    fundingId,
    sdNbf,
    privateStreamCounterNbf,
    privateStreamCounterExp,
    streamToken,
    shouldSubmitStreamPaymentOnSdPreset,
    isSubmitStreamPaymentFetching,
    isFetchStreamTokenFetching
  ) => {
    const hasStreamToken = !!streamToken;
    const isScheduledPrivateLiveStream = !!sdNbf;
    const canUnlockStreamInShowStatus = [
      privateShowStatusConstant.FUNDING,
      privateShowStatusConstant.FUNDING_FINAL_CALL,
      privateShowStatusConstant.CONSIDERING,
    ].includes(privateShowStatus);
    if (
      PRESET_SD === streamStatus &&
      !hasStreamToken &&
      !shouldSubmitStreamPaymentOnSdPreset &&
      !isSubmitStreamPaymentFetching &&
      !isFetchStreamTokenFetching &&
      (canUnlockStreamInShowStatus || !fundingId)
    ) {
      return true;
    }
    if (
      PRESET_PREVIEW === streamStatus &&
      !fundingId &&
      !hasStreamToken &&
      !shouldSubmitStreamPaymentOnSdPreset &&
      !isSubmitStreamPaymentFetching &&
      !isFetchStreamTokenFetching &&
      (isScheduledPrivateLiveStream ||
        (!!privateStreamCounterNbf && !!privateStreamCounterExp))
    ) {
      return true;
    }
    return false;
  }
)((state, streamId) => `${streamId}`);

export default getShouldShowLivestreamCta;
